import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Plus,
  Search,
  AlertCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { useSport } from '../context/SportContext';
import { SmartAthleteCard } from '../components/roster/SmartAthleteCard';
import { rosterCopy } from '../components/roster/rosterCopy';

const POSITIONS_FALLBACK = ['GK', 'CB', 'RB', 'LB', 'CDM', 'CM', 'CAM', 'RM', 'LM', 'RW', 'LW', 'ST', 'CF'];
const STATUSES = ['FULLTRAIN', 'RTP', 'OUT'];

const AthleteListPage = () => {
  const { api, hasRole } = useAuth();
  const { t, language } = useLanguage();
  const copy = rosterCopy[language] || rosterCopy.EN;
  const { positions: sportPositions } = useSport();
  const POSITIONS = sportPositions && sportPositions.length > 0 ? sportPositions : POSITIONS_FALLBACK;
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [athletes, setAthletes] = useState([]);
  const [teams, setTeams] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const requestNumber = useRef(0);
  
  // Filters
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all');
  const [positionFilter, setPositionFilter] = useState(searchParams.get('position') || 'all');
  const [teamFilter, setTeamFilter] = useState(searchParams.get('team') || 'all');
  
  // Create dialog
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newAthlete, setNewAthlete] = useState({
    first_name: '',
    last_name: '',
    birth_date: '',
    primary_position: '',
    jersey_number: '',
    status: 'FULLTRAIN',
    notes: '',
  });
  const [isCreating, setIsCreating] = useState(false);

  const canCreate = hasRole(['ADMIN', 'SNC']);

  const fetchAthletes = useCallback(async (signal) => {
    const requestId = ++requestNumber.current;
    setIsLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (statusFilter && statusFilter !== 'all') params.append('status', statusFilter);
      if (positionFilter && positionFilter !== 'all') params.append('position', positionFilter);
      if (teamFilter && teamFilter !== 'all') params.append('team_id', teamFilter);
      
      const response = await api.get(`/athletes/roster-summary?${params.toString()}`, { signal });
      if (requestId === requestNumber.current && !signal?.aborted) setAthletes(response.data.athletes);
    } catch (err) {
      if (requestId === requestNumber.current && !signal?.aborted) setError(true);
    } finally {
      if (requestId === requestNumber.current && !signal?.aborted) setIsLoading(false);
    }
  }, [api, search, statusFilter, positionFilter, teamFilter]);

  const fetchTeams = useCallback(async () => {
    try {
      const response = await api.get('/teams');
      setTeams(response.data);
    } catch (err) {
      console.error('Failed to fetch teams:', err);
    }
  }, [api]);

  useEffect(() => {
    fetchTeams();
  }, [fetchTeams]);

  useEffect(() => {
    const controller = new AbortController();
    const debounce = setTimeout(() => {
      fetchAthletes(controller.signal);
      // Update URL params
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (statusFilter !== 'all') params.set('status', statusFilter);
      if (positionFilter !== 'all') params.set('position', positionFilter);
      if (teamFilter !== 'all') params.set('team', teamFilter);
      setSearchParams(params);
    }, 300);
    return () => { clearTimeout(debounce); controller.abort(); };
  }, [search, statusFilter, positionFilter, teamFilter, fetchAthletes, setSearchParams]);

  const handleCreateAthlete = async (e) => {
    e.preventDefault();
    setIsCreating(true);
    try {
      const data = {
        ...newAthlete,
        jersey_number: newAthlete.jersey_number ? parseInt(newAthlete.jersey_number) : null,
        secondary_positions: [],
      };
      await api.post('/athletes', data);
      toast.success(t('saveSuccess'));
      setShowCreateDialog(false);
      setNewAthlete({
        first_name: '',
        last_name: '',
        birth_date: '',
        primary_position: '',
        jersey_number: '',
        status: 'FULLTRAIN',
        notes: '',
      });
      fetchAthletes();
    } catch (err) {
      toast.error(t('error'));
      console.error(err);
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="smart-roster space-y-6 fade-in" data-testid="smart-roster-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-zinc-100" data-testid="roster-heading">{t('athleteList')}</h1>
          <p className="text-sm text-zinc-500 mt-1" data-testid="roster-athlete-count">
            {athletes.length} {t('athletes').toLowerCase()}
          </p>
        </div>
        {canCreate && (
          <Button
            onClick={() => setShowCreateDialog(true)}
            className="bg-amber-500 text-zinc-950 hover:bg-amber-400 font-bold uppercase tracking-wide rounded-sm"
            data-testid="add-athlete-button"
          >
            <Plus className="w-4 h-4 mr-2" />
            {t('addAthlete')}
          </Button>
        )}
      </div>

      {/* Filters */}
      <div className="border-y border-zinc-800 py-4" data-testid="roster-filters">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <Input
              placeholder={t('search')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 bg-zinc-950 border-zinc-800 rounded-sm"
              data-testid="search-input"
            />
          </div>
          
          <Select value={teamFilter} onValueChange={setTeamFilter}>
            <SelectTrigger className="bg-zinc-950 border-zinc-800 rounded-sm" data-testid="team-filter">
              <SelectValue placeholder={t('team')} />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-800">
              <SelectItem value="all" data-testid="team-filter-all">{t('all')} {t('teams')}</SelectItem>
              {teams.map((team) => (
                <SelectItem key={team.id} value={team.id} data-testid={`team-filter-${team.id}`}>{team.code}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="bg-zinc-950 border-zinc-800 rounded-sm" data-testid="status-filter">
              <SelectValue placeholder={t('status')} />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-800">
              <SelectItem value="all" data-testid="status-filter-all">{t('all')} {t('status')}</SelectItem>
              {STATUSES.map((status) => (
                <SelectItem key={status} value={status} data-testid={`status-filter-${status}`}>{status}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Select value={positionFilter} onValueChange={setPositionFilter}>
            <SelectTrigger className="bg-zinc-950 border-zinc-800 rounded-sm" data-testid="position-filter">
              <SelectValue placeholder={t('position')} />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-800">
              <SelectItem value="all" data-testid="position-filter-all">{copy.allPositions}</SelectItem>
              {POSITIONS.map((pos) => (
                <SelectItem key={pos} value={pos} data-testid={`position-filter-${pos}`}>{pos}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* One tenant-scoped batch supplies all cards, including their expanded data. */}
      <div aria-live="polite" aria-busy={isLoading} data-testid="roster-results">
        {isLoading ? (
          <div className="p-8 text-center text-zinc-400" data-testid="roster-loading">{t('loading')}</div>
        ) : error ? (
          <div role="alert" className="p-8 text-center text-rose-400 flex flex-wrap items-center justify-center gap-2" data-testid="roster-error">
            <AlertCircle className="w-4 h-4" />
            {copy.error}
            <Button variant="outline" onClick={() => fetchAthletes()} data-testid="roster-retry">{copy.retry}</Button>
          </div>
        ) : athletes.length === 0 ? (
          <div className="p-8 text-center text-zinc-400" data-testid="roster-empty">{t('noResults')}</div>
        ) : (
          <div className="roster-grid" data-testid="roster-card-grid">
            {athletes.map(athlete => <SmartAthleteCard key={athlete.id} athlete={athlete} language={language} />)}
          </div>
        )}
      </div>

      {/* Create Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 sm:max-w-lg" data-testid="create-athlete-dialog">
          <DialogHeader>
            <DialogTitle className="text-zinc-100">{t('addAthlete')}</DialogTitle>
            <DialogDescription className="text-zinc-500">
              {language === 'IT' ? 'Dati atleta' : 'Athlete details'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateAthlete}>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="first_name" className="text-zinc-400">{t('firstName')}</Label>
                  <Input
                    id="first_name"
                    value={newAthlete.first_name}
                    onChange={(e) => setNewAthlete({ ...newAthlete, first_name: e.target.value })}
                    className="bg-zinc-950 border-zinc-800"
                    required
                    data-testid="athlete-first-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="last_name" className="text-zinc-400">{t('lastName')}</Label>
                  <Input
                    id="last_name"
                    value={newAthlete.last_name}
                    onChange={(e) => setNewAthlete({ ...newAthlete, last_name: e.target.value })}
                    className="bg-zinc-950 border-zinc-800"
                    required
                    data-testid="athlete-last-name"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="birth_date" className="text-zinc-400">{t('birthDate')}</Label>
                  <Input
                    id="birth_date"
                    type="date"
                    value={newAthlete.birth_date}
                    onChange={(e) => setNewAthlete({ ...newAthlete, birth_date: e.target.value })}
                    className="bg-zinc-950 border-zinc-800"
                    required
                    data-testid="athlete-birth-date"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="jersey_number" className="text-zinc-400">{t('jerseyNumber')}</Label>
                  <Input
                    id="jersey_number"
                    type="number"
                    min="1"
                    max="99"
                    value={newAthlete.jersey_number}
                    onChange={(e) => setNewAthlete({ ...newAthlete, jersey_number: e.target.value })}
                    className="bg-zinc-950 border-zinc-800"
                    data-testid="athlete-jersey-number"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-zinc-400">{t('primaryPosition')}</Label>
                  <Select
                    value={newAthlete.primary_position}
                    onValueChange={(val) => setNewAthlete({ ...newAthlete, primary_position: val })}
                  >
                    <SelectTrigger className="bg-zinc-950 border-zinc-800" data-testid="athlete-position">
                      <SelectValue placeholder={copy.position} />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800">
                      {POSITIONS.map((pos) => (
                        <SelectItem key={pos} value={pos} data-testid={`create-athlete-position-${pos}`}>{pos}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-400">{t('status')}</Label>
                  <Select
                    value={newAthlete.status}
                    onValueChange={(val) => setNewAthlete({ ...newAthlete, status: val })}
                  >
                    <SelectTrigger className="bg-zinc-950 border-zinc-800" data-testid="athlete-status">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800">
                      {STATUSES.map((status) => (
                        <SelectItem key={status} value={status} data-testid={`create-athlete-status-${status}`}>{status}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="notes" className="text-zinc-400">{t('notes')}</Label>
                <Textarea
                  id="notes"
                  value={newAthlete.notes}
                  onChange={(e) => setNewAthlete({ ...newAthlete, notes: e.target.value })}
                  className="bg-zinc-950 border-zinc-800 min-h-[80px]"
                  data-testid="athlete-notes"
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowCreateDialog(false)}
                data-testid="create-athlete-cancel"
                className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
              >
                {t('cancel')}
              </Button>
              <Button
                type="submit"
                disabled={isCreating || !newAthlete.first_name || !newAthlete.last_name || !newAthlete.birth_date || !newAthlete.primary_position}
                className="bg-amber-500 text-zinc-950 hover:bg-amber-400 font-bold"
                data-testid="create-athlete-submit"
              >
                {isCreating ? t('loading') : t('create')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AthleteListPage;
